import { useEffect, useState } from 'react';
import { Button, Flex, Icon, Switch, Text, message } from '@nula/core-ui';
import { ApiError } from '@/lib/http/client';
import { useCalendarStore } from '@/stores/use-calendar-store';
import type { CalendarSubscription } from '@/stores/use-calendar-store';
import { apiFetch } from '@/lib/http/client';
import { isDesktop } from '@/lib/platform';
import { openCalendarOAuth } from '@/lib/calendar/calendar-oauth';
import { RecordingsSettings } from './recordings-settings';
import styles from './meetings-tab.module.css';

// CalendarSettings is a self-contained sub-component of MeetingsTab —
// kept private (not exported) since it has no other consumers.
function CalendarSettings() {
  const { connections, fetchConnections } = useCalendarStore();
  const [subscriptions, setSubscriptions] = useState<Record<string, CalendarSubscription[]>>({});

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  useEffect(() => {
    connections.forEach(async (conn) => {
      try {
        const subs = await apiFetch<CalendarSubscription[]>(
          `/workspace/integrations/calendar/connections/${conn.id}/subscriptions`
        );
        if (Array.isArray(subs)) setSubscriptions(prev => ({ ...prev, [conn.id]: subs }));
      } catch {}
    });
  }, [connections]);

  const handleToggleSub = async (connId: string, subId: string, isSync: boolean) => {
    try {
      await apiFetch(`/workspace/integrations/calendar/connections/${connId}/subscriptions/${subId}`, {
        method: 'PATCH',
        body: JSON.stringify({ isSynced: isSync }),
      });
      setSubscriptions(prev => ({
        ...prev,
        [connId]: (prev[connId] ?? []).map(s => s.id === subId ? { ...s, isSynced: isSync } : s),
      }));
    } catch {}
  };

  const handleConnect = async (provider: 'google' | 'microsoft') => {
    try {
      await openCalendarOAuth(provider);
    } catch (err) {
      console.error('[MeetingsTab] openCalendarOAuth failed', err);
      const detail =
        err instanceof ApiError
          ? `${err.code} (${err.status})`
          : err instanceof Error
            ? err.message
            : String(err);
      message.error(`Couldn't open calendar OAuth: ${detail}`);
    }
  };

  const handleDisconnect = async (connId: string) => {
    try {
      await apiFetch(`/workspace/integrations/calendar/connections/${connId}`, { method: 'DELETE' });
      fetchConnections();
    } catch {}
  };

  const hasGoogle = connections.some(c => c.provider === 'google');
  const hasMicrosoft = connections.some(c => c.provider === 'microsoft');

  return (
    <Flex vertical gap="sm">
      {connections.map(conn => (
        <Flex key={conn.id} vertical gap="sm" className={styles.calConnCard}>
          <Flex align="center" justify="space-between">
            <Flex align="center" gap="sm">
              <Icon name={conn.provider === 'google' ? 'account_circle' : 'business'} size={16} />
              <Flex vertical>
                <Text size="sm" className={styles.calConnEmailLabel}>{conn.email}</Text>
                <Text size="xs" type="secondary">
                  {conn.provider === 'google' ? 'Google Calendar' : 'Microsoft Calendar'}
                </Text>
              </Flex>
            </Flex>
            <Button size="sm" variant="default" onClick={() => handleDisconnect(conn.id)}>
              Disconnect
            </Button>
          </Flex>
          {conn.needsReauth && (
            <Flex align="center" gap="sm" className={styles.calReauthRow}>
              <Icon name="warning" size={14} />
              <Text size="xs">Access expired — </Text>
              <Text
                size="xs"
                className={styles.calReconnectLink}
                onClick={() => handleConnect(conn.provider as 'google' | 'microsoft')}
              >
                Reconnect
              </Text>
            </Flex>
          )}
          {(subscriptions[conn.id] ?? []).map(sub => (
            <Flex key={sub.id} align="center" justify="space-between" className={styles.calSubRow}>
              <Flex align="center" gap="sm">
                {sub.color && (
                  <Flex
                    className={styles.calSubSwatch}
                    style={{ '--swatch-color': sub.color } as React.CSSProperties}
                  />
                )}
                <Text size="sm">{sub.name}</Text>
              </Flex>
              <Switch
                checked={sub.isSynced}
                onChange={(checked) => handleToggleSub(conn.id, sub.id, checked)}
                size="sm"
              />
            </Flex>
          ))}
        </Flex>
      ))}
      <Flex vertical gap="sm">
        {!hasGoogle && (
          <Flex align="center" justify="space-between" className={styles.calConnectRow}>
            <Flex align="center" gap="sm">
              <Icon name="account_circle" size={16} className={styles.calConnectIcon} />
              <Text size="sm">Google Calendar</Text>
            </Flex>
            <Button size="sm" variant="default" onClick={() => handleConnect('google')}>
              Connect
            </Button>
          </Flex>
        )}
        {!hasMicrosoft && (
          <Flex align="center" justify="space-between" className={styles.calConnectRow}>
            <Flex align="center" gap="sm">
              <Icon name="business" size={16} className={styles.calConnectIcon} />
              <Text size="sm">Microsoft Calendar</Text>
            </Flex>
            <Button size="sm" variant="default" onClick={() => handleConnect('microsoft')}>
              Connect
            </Button>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}

export function MeetingsTab() {
  return (
    <Flex vertical gap="lg">
      <Flex vertical gap="xs">
        <Text size="md" className={styles.meetingsSectionLabel}>Meetings</Text>
        <Text size="sm" type="secondary">Configure meeting capture, calendar sync, and recording preferences.</Text>
      </Flex>

      <Flex vertical gap="sm">
        <Text className={styles.sectionGroupLabel}>Calendar</Text>
        <CalendarSettings />
      </Flex>

      <Flex vertical gap="sm">
        <Text className={styles.sectionGroupLabel}>Recording</Text>
        {!isDesktop && (
          <Flex vertical gap="sm" className={styles.desktopOnlyBanner}>
            <Flex align="center" gap="sm">
              <Icon name="desktop_mac" size={16} className={styles.desktopOnlyIcon} />
              <Text size="sm" className={styles.meetingsSectionLabel}>Desktop-only features</Text>
            </Flex>
            <Text size="xs" type="secondary">
              Shortcut and device selection require the Nula desktop app.
            </Text>
          </Flex>
        )}
      </Flex>
      <RecordingsSettings desktopOnly={!isDesktop} />
    </Flex>
  );
}
